import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { formatDateToAPI } from '../../helpers/UtilsFunctions';
import { getApiUrl, COMPANY_API } from '../../helpers/url_helper';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/Warning';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Select, MenuItem, FormControl, InputLabel, TextField } from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { enGB } from 'date-fns/locale';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const TaskForm = ({ isOpen, onClose, onSubmit, projectId, editData = null, projectStartDate = null }) => {
  const { mode } = useThemeMode();
  const isEditMode = !!editData;
  
  // Custom theme for Material-UI components to match current styling
  const customTheme = createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#26C3C0',
      },
      background: {
        default: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        paper: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
      },
      text: {
        primary: mode === 'dark' ? '#FFFFFF' : '#222222',
        secondary: mode === 'dark' ? '#666' : '#999',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '& fieldset': {
                borderColor: mode === 'dark' ? '#444' : '#ddd',
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: '#26C3C0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#26C3C0',
              },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#666' : '#999',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '&.Mui-focused': {
                color: '#26C3C0',
              },
            },
          },
        },
      },
      MuiDatePicker: {
        styleOverrides: {
          root: {
            '& .MuiPickersDay-root': {
              fontFamily: "'Urbanist', Arial, sans-serif",
              '&.Mui-selected': {
                backgroundColor: '#26C3C0',
                '&:hover': {
                  backgroundColor: '#1f9e9b',
                },
              },
            },
          },
        },
      },
    },
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: '',
    status: '',
    start_date: null,
    due_date: null,
    attachments: null
  });

  const [employees, setEmployees] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingEnums, setIsLoadingEnums] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showFormLoading, setShowFormLoading] = useState(false);

  // Fetch task enums
  useEffect(() => {
    const fetchTaskEnums = async () => {
      setIsLoadingEnums(true);
      try {
        const response = await dataAPI.getTaskEnums({ showLoading: false });
        if (response.priorities && response.statuses) {
          setPriorityOptions(response.priorities);
          setStatusOptions(response.statuses);
          
          // Store task enums in localStorage for use in other components
          localStorage.setItem('taskEnums', JSON.stringify({
            priorities: response.priorities,
            statuses: response.statuses
          }));
          
          // Set default values if form is not in edit mode
          if (!editData) {
            setFormData(prev => ({
              ...prev,
              priority: response.priorities[0]?.value || '',
              status: response.statuses[0]?.value || ''
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching task enums:', error);
        setError('Failed to load task options');
      } finally {
        setIsLoadingEnums(false);
      }
    };

    fetchTaskEnums();
  }, [editData]);

  // Fetch employees list
  useEffect(() => {
    const fetchEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const response = await dataAPI.getAllEmployees({ showLoading: false });
        setEmployees(response.data || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError('Failed to load employees list');
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  // Helper function to convert plain text to HTML if needed
  const convertDescriptionToHtml = (description) => {
    if (!description) return '';
    // If it's already HTML (contains tags), return as is
    if (description.includes('<') && description.includes('>')) {
      return description;
    }
    // Convert plain text to HTML with line breaks
    return description.replace(/\n/g, '<br>');
  };

  // Initialize form data when editData changes
  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || '',
        description: convertDescriptionToHtml(editData.description || ''),
        assigned_to: editData.assigned_to || '',
        priority: editData.priority || '',
        status: editData.status || '',
        start_date: editData.start_date ? new Date(editData.start_date) : null,
        due_date: editData.due_date ? new Date(editData.due_date) : null,
        attachments: null
      });
    } else {
      setFormData({
        title: '',
        description: '',
        assigned_to: '',
        priority: '',
        status: '',
        start_date: null,
        due_date: null,
        attachments: null
      });
    }
  }, [editData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        attachments: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);
    
    // Check for validation errors before proceeding
    if (hasValidationErrors) {
      return; // Stop submission if there are validation errors
    }
    
    setIsSubmitting(true);
    setShowFormLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('assigned_to', formData.assigned_to || '');
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('start_date', formatDateToAPI(formData.start_date));
      formDataToSend.append('due_date', formatDateToAPI(formData.due_date));
      formDataToSend.append('project_id', projectId); // Always include project_id
      
      if (formData.attachments) {
        formDataToSend.append('attachments', formData.attachments);
      }

      let url, method;
      if (isEditMode && editData) {
        // Edit existing task
        url = getApiUrl(`${COMPANY_API.TASK_EDIT}/${editData.id}`);
        method = 'PUT';
        // No need to append 'id' to form data since it's in the URL
      } else {
        // Create new task
        url = getApiUrl(`${COMPANY_API.CREATE_TASK}/${projectId}`);
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(isEditMode ? 'Task updated successfully:' : 'Task created successfully:', result);
      
             // Reset form
       setFormData({
         title: '',
         description: '',
         assigned_to: '',
         priority: priorityOptions[0]?.value || '',
         status: statusOptions[0]?.value || '',
         start_date: '',
         due_date: '',
         attachments: null
       });
       
       setShowValidation(false);
       onSubmit(result);
    } catch (error) {
      console.error(isEditMode ? 'Update task error:' : 'Create task error:', error);
      setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} task. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  const handleClose = () => {
          setFormData({
        title: '',
        description: '',
        assigned_to: '',
        priority: priorityOptions[0]?.value || '',
        status: statusOptions[0]?.value || '',
        start_date: null,
        due_date: null,
        attachments: null
      });
    setError('');
    setShowValidation(false);
    setShowFormLoading(false);
    onClose();
  };

  // Check if all required fields are filled
  const requiredFields = ['title', 'assigned_to', 'start_date', 'due_date'];
  const isFormComplete = requiredFields.every(field => formData[field]);
  
  // Helper function to normalize date to midnight for date-only comparison
  const normalizeDateToMidnight = (date) => {
    if (!date) return null;
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };
  
  // Validation for dates
  const validateDates = () => {
    // Check if task start date is earlier than project start date
    if (formData.start_date && projectStartDate) {
      const taskStart = normalizeDateToMidnight(formData.start_date);
      const projectStart = normalizeDateToMidnight(projectStartDate);
      if (taskStart < projectStart) {
        return 'Task start date cannot be earlier than project start date';
      }
    }
    
    // Check if task start date is later than due date
    if (formData.start_date && formData.due_date) {
      const taskStart = normalizeDateToMidnight(formData.start_date);
      const taskDue = normalizeDateToMidnight(formData.due_date);
      if (taskStart > taskDue) {
        return 'Start date cannot be later than due date';
      }
    }
    
    // Check if task due date is earlier than project start date
    if (formData.due_date && projectStartDate) {
      const taskDue = normalizeDateToMidnight(formData.due_date);
      const projectStart = normalizeDateToMidnight(projectStartDate);
      if (taskDue < projectStart) {
        return 'Task due date cannot be earlier than project start date';
      }
    }
    return null;
  };
  
  const dateValidationError = validateDates();
  const hasValidationErrors = dateValidationError;

  if (!isOpen) return null;

     return (
     <ThemeProvider theme={customTheme}>
       <>
         <style>
           {`
             @keyframes spin {
               0% { transform: rotate(0deg); }
               100% { transform: rotate(360deg); }
             }
             
             .hide-scrollbar::-webkit-scrollbar {
               display: none;
             }
             .hide-scrollbar {
               -ms-overflow-style: none;
               scrollbar-width: none;
             }
             
             /* ReactQuill custom styles */
             .ql-editor {
               background-color: ${mode === 'dark' ? '#2a2a2a' : '#ffffff'} !important;
               color: ${mode === 'dark' ? '#ffffff' : '#000000'} !important;
               font-family: 'Urbanist', Arial, sans-serif !important;
               font-size: 14px !important;
               line-height: 1.6 !important;
               min-height: 80px !important;
               max-height: 120px !important;
               overflow-y: auto !important;
             }
             .ql-editor * {
               font-family: 'Urbanist', Arial, sans-serif !important;
             }
             .ql-editor p, .ql-editor div, .ql-editor span, .ql-editor strong, .ql-editor em, .ql-editor ul, .ql-editor ol, .ql-editor li {
               font-family: 'Urbanist', Arial, sans-serif !important;
               font-size: 14px !important;
             }
             /* More aggressive font overrides for ReactQuill */
             .ql-editor[contenteditable="true"] {
               font-family: 'Urbanist', Arial, sans-serif !important;
             }
             .ql-editor[contenteditable="true"] * {
               font-family: 'Urbanist', Arial, sans-serif !important;
             }
             .ql-editor.ql-blank {
               font-family: 'Urbanist', Arial, sans-serif !important;
             }
             .ql-editor.ql-blank::before {
               font-family: 'Urbanist', Arial, sans-serif !important;
             }
             .ql-editor::-webkit-scrollbar {
               width: 6px !important;
             }
             .ql-editor::-webkit-scrollbar-track {
               background: ${mode === 'dark' ? '#1a1a1a' : '#f1f1f1'} !important;
               border-radius: 3px !important;
             }
             .ql-editor::-webkit-scrollbar-thumb {
               background: ${mode === 'dark' ? '#555' : '#c1c1c1'} !important;
               border-radius: 3px !important;
             }
             .ql-editor::-webkit-scrollbar-thumb:hover {
               background: ${mode === 'dark' ? '#777' : '#a1a1a1'} !important;
             }
             .ql-toolbar {
               background-color: ${mode === 'dark' ? '#333' : '#f8f9fa'} !important;
               border-color: ${mode === 'dark' ? '#444' : '#ddd'} !important;
               border-radius: 8px 8px 0 0 !important;
               padding: 8px 12px !important;
             }
             .ql-container {
               border-color: ${mode === 'dark' ? '#444' : '#ddd'} !important;
               border-radius: 0 0 8px 8px !important;
             }
             .ql-toolbar .ql-stroke {
               stroke: ${mode === 'dark' ? '#ffffff' : '#444'} !important;
             }
             .ql-toolbar .ql-fill {
               fill: ${mode === 'dark' ? '#ffffff' : '#444'} !important;
             }
             .ql-toolbar button {
               border: 1px solid ${mode === 'dark' ? '#555' : '#ccc'} !important;
               border-radius: 6px !important;
               background-color: ${mode === 'dark' ? '#444' : '#ffffff'} !important;
               margin: 0 2px !important;
               padding: 6px 8px !important;
               height: 32px !important;
               min-width: 32px !important;
               transition: all 0.2s ease !important;
               box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
             }
             .ql-toolbar button:hover {
               background-color: ${mode === 'dark' ? '#555' : '#f0f0f0'} !important;
               border-color: #26C3C0 !important;
               box-shadow: 0 2px 6px rgba(38, 195, 192, 0.2) !important;
             }
             .ql-toolbar button.ql-active {
               background-color: #26C3C0 !important;
               border-color: #26C3C0 !important;
               color: white !important;
               box-shadow: 0 2px 8px rgba(38, 195, 192, 0.3) !important;
             }
             .ql-toolbar button.ql-active .ql-stroke {
               stroke: white !important;
             }
             .ql-toolbar button.ql-active .ql-fill {
               fill: white !important;
             }
             
             /* ReactQuill placeholder styling */
             .ql-editor.ql-blank::before {
               color: ${mode === 'dark' ? '#B0B0B0' : '#999999'} !important;
               font-style: normal !important;
               font-family: 'Urbanist', Arial, sans-serif !important;
               font-size: 14px !important;
             }
           `}
         </style>
         {/* Backdrop */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
        />
        
                 {/* Modal */}
         <div 
           style={{
             position: 'fixed',
             top: '20px',
             right: '20px',
             width: '420px',
             height: 'calc(100vh - 40px)',
             backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
             boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
             zIndex: 1001,
             overflowY: 'auto',
             transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
             transition: 'transform 0.3s ease-in-out',
             borderRadius: '12px',
             scrollbarWidth: 'none',
             msOverflowStyle: 'none'
           }}
           className="hide-scrollbar"
         >
           {/* Modal Content Wrapper */}
           <div style={{ position: 'relative', height: '100%' }}>
             {/* Header */}
             <div style={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               padding: '12px 20px 8px 20px',
               borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
             }}>
               <h2 style={{
                 fontSize: '18px',
                 fontWeight: '600',
                 color: mode === 'dark' ? '#FFFFFF' : '#222222',
                 margin: 0,
                 fontFamily: "'Urbanist', Arial, sans-serif"
               }}>
                 {isEditMode ? 'Edit Task' : 'Create Task'}
               </h2>
               <button
                 onClick={handleClose}
                 style={{
                   background: 'none',
                   border: 'none',
                   cursor: 'pointer',
                   padding: '6px',
                   borderRadius: '4px',
                   color: mode === 'dark' ? '#FFFFFF' : '#666666',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center'
                 }}
               >
                 <CloseIcon style={{ fontSize: '20px' }} />
               </button>
             </div>


        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '16px 20px' }}>
          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '16px',
              backgroundColor: '#1a0000',
              border: '1px solid #ff4444',
              borderRadius: '8px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <WarningIcon style={{
                  color: '#ff4444',
                  fontSize: '20px'
                }} />
                <span style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  lineHeight: '1.4'
                }}>
                  {error}
                </span>
              </div>
            </div>
          )}

          {/* Task Title */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label="Enter Task Title *"
              variant="outlined"
              fullWidth
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                  fontSize: '14px',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  height: '47px',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#666' : '#999',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  fontSize: '14px',
                  '&.Mui-focused': {
                    color: '#26C3C0',
                  },
                },
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontFamily: "'Urbanist', Arial, sans-serif",
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              fontWeight: '500'
            }}>
              Task Description
            </label>
            <ReactQuill
              value={formData.description || ''}
              onChange={(value) => handleInputChange('description', value)}
              placeholder="Enter task description..."
              modules={{
                toolbar: [
                  ['bold', 'italic'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }]
                ]
              }}
              formats={['bold', 'italic', 'list', 'bullet']}
              theme="snow"
              style={{
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                color: mode === 'dark' ? '#ffffff' : '#000000',
                borderRadius: '8px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                maxHeight: '150px',
                fontFamily: "'Urbanist', Arial, sans-serif"
              }}
              sx={{
                '& .ql-editor': {
                  fontFamily: 'Urbanist, Arial, sans-serif !important',
                  fontSize: '14px !important',
                  color: mode === 'dark' ? '#FFFFFF' : '#000000'
                },
                '& .ql-editor *': {
                  fontFamily: 'Urbanist, Arial, sans-serif !important'
                },
                '& .ql-editor p, & .ql-editor div, & .ql-editor span, & .ql-editor strong, & .ql-editor em, & .ql-editor ul, & .ql-editor ol, & .ql-editor li': {
                  fontFamily: 'Urbanist, Arial, sans-serif !important',
                  fontSize: '14px !important'
                }
              }}
            />
          </div>

                     {/* Assigned To */}
           <div style={{ marginBottom: '16px' }}>
             <FormControl fullWidth>
                               <InputLabel>Assigned To *</InputLabel>
               <Select
                 value={formData.assigned_to}
                 onChange={(e) => handleInputChange('assigned_to', e.target.value)}
                                   label="Assigned To *"
                 sx={{
                   '& .MuiOutlinedInput-root': {
                     borderRadius: '6px',
                     backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                     color: mode === 'dark' ? '#FFFFFF' : '#222222',
                     fontSize: '11px',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     height: '44px',
                     '& fieldset': {
                       borderColor: mode === 'dark' ? '#444' : '#ddd',
                     },
                     '&:hover fieldset': {
                       borderColor: '#26C3C0',
                     },
                     '&.Mui-focused fieldset': {
                       borderColor: '#26C3C0',
                     },
                   },
                   '& .MuiInputLabel-root': {
                     color: mode === 'dark' ? '#666' : '#999',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     fontSize: '11px',
                     '&.Mui-focused': {
                       color: '#26C3C0',
                     },
                   },
                 }}
               >
                 {employees.map((employee) => (
                   <MenuItem key={employee.id} value={employee.id}>
                     {employee.full_name || employee.username || `Employee ${employee.id}`}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
           </div>

                     {/* Priority */}
           <div style={{ marginBottom: '16px' }}>
             <FormControl fullWidth>
               <InputLabel>Select Priority</InputLabel>
               <Select
                 value={formData.priority}
                 onChange={(e) => handleInputChange('priority', e.target.value)}
                 label="Select Priority"
                 disabled={isLoadingEnums}
                 sx={{
                   '& .MuiOutlinedInput-root': {
                     borderRadius: '6px',
                     backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                     color: mode === 'dark' ? '#FFFFFF' : '#222222',
                     fontSize: '11px',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     height: '44px',
                     '& fieldset': {
                       borderColor: mode === 'dark' ? '#444' : '#ddd',
                     },
                     '&:hover fieldset': {
                       borderColor: '#26C3C0',
                     },
                     '&.Mui-focused fieldset': {
                       borderColor: '#26C3C0',
                     },
                   },
                   '& .MuiInputLabel-root': {
                     color: mode === 'dark' ? '#666' : '#999',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     fontSize: '11px',
                     '&.Mui-focused': {
                       color: '#26C3C0',
                     },
                   },
                 }}
               >
                 {isLoadingEnums ? (
                   <MenuItem disabled>Loading priorities...</MenuItem>
                 ) : (
                   priorityOptions.map((option) => (
                     <MenuItem key={option.value} value={option.value}>
                       {option.label}
                     </MenuItem>
                   ))
                 )}
               </Select>
             </FormControl>
           </div>

                     {/* Status */}
           <div style={{ marginBottom: '16px' }}>
             <FormControl fullWidth>
               <InputLabel>Select Status</InputLabel>
               <Select
                 value={formData.status}
                 onChange={(e) => handleInputChange('status', e.target.value)}
                 label="Select Status"
                 disabled={isLoadingEnums}
                 sx={{
                   '& .MuiOutlinedInput-root': {
                     borderRadius: '6px',
                     backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                     color: mode === 'dark' ? '#FFFFFF' : '#222222',
                     fontSize: '11px',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     height: '44px',
                     '& fieldset': {
                       borderColor: mode === 'dark' ? '#444' : '#ddd',
                     },
                     '&:hover fieldset': {
                       borderColor: '#26C3C0',
                     },
                     '&.Mui-focused fieldset': {
                       borderColor: '#26C3C0',
                     },
                   },
                   '& .MuiInputLabel-root': {
                     color: mode === 'dark' ? '#666' : '#999',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     fontSize: '11px',
                     '&.Mui-focused': {
                       color: '#26C3C0',
                     },
                   },
                 }}
               >
                 {isLoadingEnums ? (
                   <MenuItem disabled>Loading statuses...</MenuItem>
                 ) : (
                   statusOptions.map((option) => (
                     <MenuItem key={option.value} value={option.value}>
                       {option.label}
                     </MenuItem>
                   ))
                 )}
               </Select>
             </FormControl>
           </div>

                     {/* Start Date */}
           <div style={{ marginBottom: '16px' }}>
             <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
               <DatePicker
                 value={formData.start_date}
                 onChange={(newValue) => {
                   handleInputChange('start_date', newValue);
                 }}
                 format="dd-MM-yyyy"
                 minDate={projectStartDate ? new Date(projectStartDate) : undefined}
                                   slotProps={{
                    textField: {
                      label: "Start Date *",
                      fullWidth: true,
                      error: showValidation && !formData.start_date,
                      helperText: showValidation && !formData.start_date ? 'Start date is required' : '',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222',
                          fontSize: '14px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          height: '47px',
                          '& fieldset': {
                            borderColor: (showValidation && !formData.start_date) ? '#f44336' : (mode === 'dark' ? '#444' : '#ddd'),
                          },
                          '&:hover fieldset': {
                            borderColor: (showValidation && !formData.start_date) ? '#f44336' : '#26C3C0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: (showValidation && !formData.start_date) ? '#f44336' : '#26C3C0',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '10px 16px',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#f44336',
                          fontSize: '12px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          marginLeft: '0px'
                        },
                      },
                    },
                  }}
               />
             </LocalizationProvider>
           </div>

                     {/* Due Date */}
           <div style={{ marginBottom: '16px' }}>
             <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
               <DatePicker
                 value={formData.due_date}
                 onChange={(newValue) => {
                   handleInputChange('due_date', newValue);
                 }}
                 format="dd-MM-yyyy"
                 minDate={projectStartDate ? new Date(projectStartDate) : undefined}
                                  slotProps={{
                    textField: {
                      label: "Due Date *",
                      fullWidth: true,
                      error: showValidation && !formData.due_date,
                      helperText: showValidation && !formData.due_date ? 'Due date is required' : '',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222',
                          fontSize: '14px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          height: '47px',
                          '& fieldset': {
                            borderColor: (showValidation && !formData.due_date) ? '#f44336' : (mode === 'dark' ? '#444' : '#ddd'),
                          },
                          '&:hover fieldset': {
                            borderColor: (showValidation && !formData.due_date) ? '#f44336' : '#26C3C0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: (showValidation && !formData.due_date) ? '#f44336' : '#26C3C0',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '10px 16px',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#f44336',
                          fontSize: '12px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          marginLeft: '0px'
                        },
                      },
                    },
                  }}
               />
             </LocalizationProvider>
           </div>

                                                           {/* Date Validation Error */}
            {dateValidationError && (
              <div style={{
                backgroundColor: mode === 'dark' ? '#1a0000' : '#ffebee',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)'
              }}>
                <WarningIcon style={{
                  color: '#ff4444',
                  fontSize: '20px'
                }} />
                <span style={{
                  color: mode === 'dark' ? '#ffffff' : '#d32f2f',
                  fontSize: '14px',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  lineHeight: '1.4'
                }}>
                  {dateValidationError}
                </span>
              </div>
            )}

          {/* File Upload */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              border: `2px dashed ${mode === 'dark' ? '#444' : '#ddd'}`,
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
              position: 'relative',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease',
              '&:hover': {
                borderColor: '#26C3C0'
              }
            }}>
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <CloudUploadIcon style={{
                fontSize: '42px',
                color: '#26C3C0',
                marginBottom: '12px'
              }} />
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#26C3C0',
                marginBottom: '6px',
                fontFamily: "'Urbanist', Arial, sans-serif"
              }}>
                Click to Upload Attachment
              </div>
              <div style={{
                fontSize: '12px',
                color: mode === 'dark' ? '#999' : '#666',
                fontFamily: "'Urbanist', Arial, sans-serif"
              }}>
                Supported file types: .pdf, .doc, .docx, .txt, .jpg, .jpeg, .png
              </div>
              {formData.attachments && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '14px',
                  color: '#26C3C0',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  Selected: {formData.attachments.name}
                </div>
              )}
            </div>
          </div>

                     {/* Submit Button */}
                       <button
              type="submit"
              disabled={isSubmitting || !isFormComplete || hasValidationErrors}
              style={{
                width: '100%',
                padding: '14px 24px',
                backgroundColor: (isFormComplete && !isSubmitting && !hasValidationErrors) ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: (isFormComplete && !isSubmitting && !hasValidationErrors) ? 'pointer' : 'not-allowed',
                fontFamily: "'Urbanist', Arial, sans-serif",
                transition: 'background-color 0.2s ease',
                boxShadow: (isFormComplete && !isSubmitting && !hasValidationErrors) ? '0 2px 8px rgba(38, 195, 192, 0.3)' : 'none',
                opacity: isSubmitting ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (isFormComplete && !isSubmitting && !hasValidationErrors) {
                  e.target.style.backgroundColor = '#22B0AD';
                } else {
                  e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#777';
                }
              }}
              onMouseLeave={(e) => {
                if (isFormComplete && !isSubmitting && !hasValidationErrors) {
                  e.target.style.backgroundColor = '#26C3C0';
                } else {
                  e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#666';
                }
              }}
            >
             {isSubmitting ? (
               <div style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 gap: '12px'
               }}>
                 {/* Mini Circular Progress */}
                 <div style={{
                   width: '20px',
                   height: '20px',
                   border: '2px solid rgba(255, 255, 255, 0.3)',
                   borderTop: '2px solid #FFFFFF',
                   borderRadius: '50%',
                   animation: 'spin 1s linear infinite'
                 }} />
                 <span>{isEditMode ? 'Updating Task...' : 'Creating Task...'}</span>
               </div>
             ) : (
               isEditMode ? 'Update Task' : 'Create Task'
             )}
           </button>
        </form>
           </div>
         </div>
      </>
    </ThemeProvider>
  );
};

export default TaskForm;
